import { useEffect, useRef, useState, type FormEvent } from 'react'
import type { FormBlock, FormField } from '@publiqa/shared'
import { ApiError, upload } from '../../lib/api'
import { lines } from '../../lib/richtext'
import { usePublic } from '../context'
import { SectionHead } from './Blocks'
import { Picture } from '../ui'
import { useRevealOnLink } from './reveal'

const ACCEPT = '.pdf,.jpg,.jpeg,.png,.webp,.heic,.doc,.docx,.odt'
const MAX_MB = 8

/** Amène `el` au milieu de l'écran et lui donne le focus (lecteurs d'écran compris). */
function bringIntoView(el: HTMLElement | null) {
  if (!el) return
  el.focus({ preventScroll: true })
  // Saut direct (« instant ») : un défilement doux serait interrompu par le
  // changement de hauteur de la page, ou par le scroll-behavior du site.
  const r = el.getBoundingClientRect()
  const top = window.scrollY + r.top - Math.max(16, (window.innerHeight - r.height) / 2)
  window.scrollTo({ top: Math.max(0, top), behavior: 'instant' })
}

function Field({ f, error }: { f: FormField; error?: string }) {
  if (f.type === 'section') return <h3 className="form-section">{f.label}</h3>
  const id = `f-${f.id}`
  const common = {
    id,
    name: f.id,
    required: f.required,
    'aria-invalid': error ? true : undefined,
    'aria-describedby': error ? `${id}-err` : undefined,
  }
  const label = (
    <>
      {f.label}
      {f.required && (
        <span className="req" aria-hidden="true">
          {' '}
          *
        </span>
      )}
    </>
  )

  if (f.type === 'checkbox') {
    return (
      <div className={`field field-check${error ? ' has-error' : ''}`}>
        <label>
          <input type="checkbox" value="1" {...common} /> <span>{label}</span>
        </label>
        {error && (
          <p className="field-error" id={`${id}-err`}>
            {error}
          </p>
        )}
      </div>
    )
  }

  return (
    <div className={`field field-${f.type}${error ? ' has-error' : ''}`}>
      <label htmlFor={id}>{label}</label>
      {f.type === 'textarea' ? (
        <textarea rows={5} placeholder={f.placeholder} {...common} />
      ) : f.type === 'select' ? (
        <select defaultValue="" {...common}>
          <option value="" disabled>
            {f.placeholder || 'Choisir…'}
          </option>
          {lines(f.options).map((o) => (
            <option key={o}>{o}</option>
          ))}
        </select>
      ) : f.type === 'file' ? (
        <FileDrop accept={ACCEPT} common={common} />
      ) : f.type === 'photo' ? (
        <FileDrop accept="image/jpeg,image/png,image/webp" common={common} />
      ) : (
        <input
          type={f.type}
          placeholder={f.placeholder}
          autoComplete={f.type === 'email' ? 'email' : f.type === 'tel' ? 'tel' : undefined}
          {...common}
        />
      )}
      {error && (
        <p className="field-error" id={`${id}-err`}>
          {error}
        </p>
      )}
    </div>
  )
}

/** Pièce jointe : zone à toucher, nom du fichier choisi affiché. */
function FileDrop({
  accept,
  common,
}: {
  accept: string
  common: { id: string; name: string; required: boolean }
}) {
  const [name, setName] = useState('')
  return (
    <label className={`field-drop${name ? ' has-file' : ''}`}>
      <input
        type="file"
        accept={accept}
        {...common}
        onChange={(e) => setName(e.target.files?.[0]?.name ?? '')}
      />
      <span className="field-drop-icon" aria-hidden="true">
        {name ? '📎' : '⇪'}
      </span>
      <span>
        <b>{name || 'Ajouter un fichier'}</b>
        <small>PDF, image ou document Word — {MAX_MB} Mo maximum.</small>
      </span>
    </label>
  )
}

interface FormBodyProps {
  fields: FormField[]
  submitLabel: string
  successMessage: string
  /** Chemin d'API (après /api/v1) qui reçoit l'envoi multipart. */
  endpoint: string
  againLabel: string
}

/** Formulaire public générique : contact comme inscription en ligne. */
function FormBody({
  fields: defs,
  submitLabel,
  successMessage,
  endpoint,
  againLabel,
}: FormBodyProps) {
  const { inert } = usePublic()
  const ref = useRef<HTMLFormElement>(null)
  // Temps passé sur le formulaire : distingue une personne d'un robot (voir l'API).
  const shownAt = useRef(Date.now())
  const success = useRef<HTMLDivElement>(null)
  const [state, setState] = useState<'idle' | 'sending' | 'done'>('idle')
  const [progress, setProgress] = useState(0)
  const [error, setError] = useState('')
  const [fields, setFields] = useState<Record<string, string>>({})

  // Le long formulaire laisse place à un court message : sans cela, la page
  // raccourcit sous le lecteur et il se retrouve en bas, sans focus.
  useEffect(() => {
    if (state === 'done') bringIntoView(success.current)
  }, [state])
  // Erreur : focus sur le premier champ refusé, sinon sur le message.
  useEffect(() => {
    if (!error && !Object.keys(fields).length) return
    const form = ref.current
    bringIntoView(
      form?.querySelector<HTMLElement>('[aria-invalid="true"]') ??
        form?.querySelector<HTMLElement>('.form-error') ??
        null,
    )
  }, [error, fields])

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (inert) return
    const data = new FormData(e.currentTarget)
    data.set('_t', String(Date.now() - shownAt.current))
    // Contrôle de taille côté client : inutile d'envoyer 30 Mo pour un refus.
    for (const f of defs.filter((x) => x.type === 'file' || x.type === 'photo')) {
      const file = data.get(f.id)
      if (file instanceof File && file.size > MAX_MB * 1024 * 1024) {
        setFields({ [f.id]: `Fichier trop lourd (${MAX_MB} Mo maximum).` })
        return
      }
      if (file instanceof File && !file.size) data.delete(f.id)
    }
    setState('sending')
    setError('')
    setFields({})
    try {
      await upload(endpoint, data, setProgress)
      setState('done')
      ref.current?.reset()
    } catch (err) {
      setState('idle')
      if (err instanceof ApiError) {
        setError(err.message)
        setFields(err.fields)
      } else setError('Envoi impossible. Réessayez dans un instant.')
    }
  }

  if (state === 'done') {
    return (
      <div className="form-success" role="status" tabIndex={-1} ref={success}>
        <span className="form-success-icon" aria-hidden="true">
          ✓
        </span>
        <p>{successMessage}</p>
        <button type="button" className="btn btn-ghost" onClick={() => setState('idle')}>
          {againLabel}
        </button>
      </div>
    )
  }
  return (
    <form ref={ref} className="form" onSubmit={submit} noValidate={false}>
      {defs.map((f) => (
        <Field key={f.id} f={f} error={fields[f.id]} />
      ))}
      {/* Pot de miel anti-robots : invisible et hors tabulation. */}
      <div className="hp" aria-hidden="true">
        <label>
          Ne pas remplir <input type="text" name="_hp" tabIndex={-1} autoComplete="off" />
        </label>
      </div>
      {error && (
        <p className="form-error" role="alert" tabIndex={-1}>
          {error}
        </p>
      )}
      <button type="submit" className="btn btn-primary form-submit" disabled={state === 'sending'}>
        {state === 'sending' ? (
          `Envoi… ${progress ? `${progress} %` : ''}`
        ) : (
          <>
            {submitLabel} <span aria-hidden="true">→</span>
          </>
        )}
      </button>
    </form>
  )
}

export function FormView({ b, pageId }: { b: FormBlock; pageId: string }) {
  const { publication, inert } = usePublic()
  // « Masqué jusqu'au clic » : visible seulement après un lien vers son ancre
  // (« Nous écrire »…) ; toujours visible dans l'éditeur.
  const { open, close } = useRevealOnLink(b.id, !!b.onDemand && !inert)
  if (b.onDemand && !inert && !open) return null
  return (
    <section className={`b-section b-form fm${b.image ? ' fm-has-img' : ''}`}>
      <div className="container">
        <div className="fm-card">
          {b.image && (
            <div className="fm-media">
              <Picture image={b.image} />
            </div>
          )}
          <div className="fm-main">
            {b.onDemand && !inert && (
              <button type="button" className="fm-close" aria-label="Fermer" onClick={close}>
                ✕
              </button>
            )}
            <SectionHead title={b.title} intro={b.intro} />
            <FormBody
              fields={b.fields}
              submitLabel={b.submitLabel}
              successMessage={b.successMessage}
              endpoint={`/public/p/${publication.slug}/forms/${pageId}/${b.id}`}
              againLabel="Envoyer un autre message"
            />
          </div>
        </div>
      </div>
    </section>
  )
}
